import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { DataTable, type DataTableColumn } from "@/components/table";
import { Button, ErrorNote, useCanWrite } from "@/components/ui";
import { ScheduleOpenPanel, scheduledKey } from "@/components/schedule-open";
import { api, errorMessage } from "@/lib/api";
import { dateTime } from "@/lib/format";
import type { ScheduledOpenFilter, ScheduledOpenItem } from "@/lib/types";

/*
 * OPERAZIONI PROGRAMMATE (26/10): tutte le aperture programmate del tenant.
 *
 * L'ordine lo da' il backend: in attesa per ora crescente (la prossima in
 * alto), le altre dalla piu' recente. Modifica solo in attesa; Annulla anche
 * durante la connessione dei conti (gli ultimi 3 minuti), che restano collegati.
 * Una riga ferma «in esecuzione» oltre i 2 minuti vuol dire backend fermato a
 * meta': l'esito non e' registrato, e si dice cosi', non «aperta» ne' «saltata».
 */

const FILTERS: { key: ScheduledOpenFilter; label: string }[] = [
  { key: "PENDING", label: "In attesa" },
  { key: "DONE", label: "Eseguite" },
  { key: "SKIPPED", label: "Saltate" },
  { key: "CANCELLED", label: "Annullate" },
  { key: "ALL", label: "Tutte" },
];

const STALE_MS = 2 * 60_000;

function statusOf(i: ScheduledOpenItem): { label: string; tone: string } {
  switch (i.status) {
    case "PENDING":
      return { label: "In attesa", tone: "text-ink" };
    case "CONNECTING":
      return { label: "Connessione dei conti", tone: "text-warn" };
    case "EXECUTING":
      return Date.now() - new Date(i.runAt).getTime() > STALE_MS
        ? { label: "Esito non registrato", tone: "text-loss" }
        : { label: "In esecuzione", tone: "text-warn" };
    case "DONE":
      return { label: "Eseguita", tone: "text-gain" };
    case "SKIPPED":
      return { label: "Saltata", tone: "text-loss" };
    case "CANCELLED":
      return { label: "Annullata", tone: "text-faint" };
  }
}

const esitoOf = (i: ScheduledOpenItem) =>
  i.status === "EXECUTING" && Date.now() - new Date(i.runAt).getTime() > STALE_MS ? "Esito non registrato, verifica la Fase" : i.resultNote ?? "";

export function ScheduledOpens() {
  const canWrite = useCanWrite();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<ScheduledOpenFilter>("PENDING");
  const [editing, setEditing] = useState<ScheduledOpenItem | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: [...scheduledKey, "list", filter],
    queryFn: () => api.scheduledOpens.list({ status: filter }),
    refetchInterval: 15_000,
  });

  const annulla = async (id: string) => {
    setBusy(id);
    setError(null);
    try {
      await api.scheduledOpens.cancel(id);
      await qc.invalidateQueries({ queryKey: [...scheduledKey] });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  };

  const pill = (active: boolean) =>
    `inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${active ? "border-accent text-ink" : "border-line text-muted hover:text-ink"}`;

  const columns: DataTableColumn<ScheduledOpenItem>[] = [
    { key: "when", header: "Quando", cell: (i) => <span className="tabular-nums">{dateTime(i.runAt)}</span> },
    { key: "client", header: "Cliente", cell: (i) => i.session?.clientName ?? "—" },
    { key: "fase", header: "Fase", cell: (i) => (i.session ? `${i.session.label}${i.session.deleted ? " (eliminata)" : ""}` : "—") },
    { key: "firm", header: "Prop firm", cell: (i) => i.session?.propFirm?.name ?? "—" },
    { key: "symbol", header: "Simbolo", cell: (i) => i.canonicalSymbol ?? "—" },
    { key: "dir", header: "Direzione", cell: (i) => (i.direction === "LONG" ? "Long" : i.direction === "SHORT" ? "Short" : "—") },
    {
      key: "lots",
      header: "Lotti / molt.",
      cell: (i) => (
        <span className="tabular-nums">
          {i.propLots ?? "—"} × {i.brokerMultiplier ?? (i.session?.kind === "BB" ? `${i.session.hedgeRatio ?? "—"} (Fase)` : "—")}
        </span>
      ),
    },
    {
      key: "status",
      header: "Stato",
      cell: (i) => {
        const st = statusOf(i);
        return <span className={st.tone}>{st.label}</span>;
      },
    },
    { key: "result", header: "Esito", cell: (i) => <span className="text-xs text-muted">{esitoOf(i)}</span> },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (i) => (
        <div className="flex justify-end gap-1.5 whitespace-nowrap">
          {canWrite && i.status === "PENDING" && i.session && (
            <Button size="sm" onClick={() => setEditing(i)}>
              Modifica
            </Button>
          )}
          {canWrite && (i.status === "PENDING" || i.status === "CONNECTING") &&
            (confirming === i.id ? (
              <Button size="sm" variant="danger" disabled={busy === i.id} onClick={() => void annulla(i.id)}>
                {busy === i.id ? "Annullamento…" : "Conferma annulla"}
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setConfirming(i.id)}>
                Annulla
              </Button>
            ))}
          <Link to={`/fasi/${i.sessionId}`} className="rounded px-2 py-1 text-xs text-muted hover:bg-raised hover:text-ink">
            Vai
          </Link>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Operazioni programmate" subtitle="Aperture che il gestionale fa da solo all'ora scelta (ora italiana), con gli stessi controlli di «Apri operazione»" />
      <div className="space-y-4 px-6 py-4">
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} className={pill(filter === f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        {error && <ErrorNote>{error}</ErrorNote>}
        {list.isLoading ? (
          <p className="text-sm text-muted">Caricamento…</p>
        ) : list.error ? (
          <ErrorNote>{errorMessage(list.error)}</ErrorNote>
        ) : (
          <DataTable
            bare
            columns={columns}
            rows={list.data?.items ?? []}
            rowKey={(i) => i.id}
            empty={{
              title: filter === "PENDING" ? "Nessuna apertura programmata in attesa" : "Nessuna programmata con questo filtro",
              hint: "Si programma dalla pagina Operazione: menu «…» della Fase, «Programma apertura».",
            }}
          />
        )}
      </div>
      {editing?.session && (
        <ScheduleOpenPanel
          target={{
            id: editing.sessionId,
            kind: editing.session.kind,
            clientName: editing.session.clientName,
            canonicalSymbol: editing.canonicalSymbol ?? "",
            propCurrency: editing.session.propCurrency,
            hedgeRatio: editing.session.hedgeRatio,
          }}
          existing={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}
